import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { factories, models, Report, service } from 'powerbi-client';
import { firstValueFrom } from 'rxjs';

import { AdminComponentBase, PageSimpleContentComponent } from 'app/layout/content';

import { PowerBiReportService } from './power-bi-report.service';

@Component({
    templateUrl: './power-bi-report-page.component.html',
    styleUrls: ['./power-bi-report-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent]
})

export class PowerBiReportPageComponent extends AdminComponentBase implements AfterViewInit, OnDestroy {
    private readonly powerBiReportService = inject(PowerBiReportService);
    private readonly powerBiService = new service.Service(
        factories.hpmFactory,
        factories.wpmpFactory,
        factories.routerFactory
    );

    @ViewChild('reportContainer') private reportContainer?: ElementRef<HTMLDivElement>;

    reportUnavailable = false;

    private embeddedReport: Report | null = null;
    private tokenRefreshTimer: number | null = null;
    private isRefreshingToken = false;
    private destroyed = false;
    private readonly tokenRefreshIntervalMs = 55 * 60 * 1000;

    ngAfterViewInit(): void {
        void this.loadReport();
    }

    async loadReport(): Promise<void> {
        if (this.embeddedReport) {
            return;
        }

        this.reportUnavailable = false;
        this.clearTokenRefreshTimer();

        try {
            const data = await firstValueFrom(this.powerBiReportService.getEmbedConfig());
            if (this.destroyed) {
                return;
            }

            const container = this.reportContainer?.nativeElement;
            if (!container) {
                this.showReportUnavailable();
                return;
            }

            this.powerBiService.reset(container);
            const report = this.powerBiService.embed(container, {
                type: 'report',
                tokenType: models.TokenType.Embed,
                accessToken: data.token,
                embedUrl: data.embedUrl,
                id: data.reportId,
                permissions: models.Permissions.Read,
                settings: {
                    navContentPaneEnabled: false,
                    filterPaneEnabled: true,
                    background: models.BackgroundType.Default,
                }
            }) as Report;

            this.embeddedReport = report;

            report.on('loaded', () => {
                if (this.destroyed || this.embeddedReport !== report) {
                    return;
                }
                this.reportUnavailable = false;
            });
            report.on('error', () => {
                if (this.destroyed || this.embeddedReport !== report) {
                    return;
                }
                this.showReportUnavailable();
            });

            this.scheduleTokenRefresh(this.getTokenRefreshDelay(data.expiration));
        } catch {
            if (!this.destroyed) {
                this.showReportUnavailable();
            }
        }
    }

    private getTokenRefreshDelay(expiration: string): number {
        const expirationTime = Date.parse(expiration);

        if (!Number.isFinite(expirationTime) || expirationTime <= Date.now()) {
            throw new Error('Graph returned an invalid or expired Power BI token expiration.');
        }

        return Math.max(1_000, expirationTime - Date.now() - 5 * 60_000);
    }

    private scheduleTokenRefresh(delayMs = this.tokenRefreshIntervalMs): void {
        this.clearTokenRefreshTimer();
        this.tokenRefreshTimer = window.setTimeout(() => {
            void this.refreshEmbedToken();
        }, delayMs);
    }

    private async refreshEmbedToken(): Promise<void> {
        if (this.destroyed || !this.embeddedReport || this.isRefreshingToken) {
            return;
        }

        this.isRefreshingToken = true;
        const report = this.embeddedReport;
        try {
            const data = await firstValueFrom(this.powerBiReportService.getEmbedConfig());
            if (this.destroyed || this.embeddedReport !== report) {
                return;
            }
            await report.setAccessToken(data.token);
            this.reportUnavailable = false;
            this.scheduleTokenRefresh(this.getTokenRefreshDelay(data.expiration));
        } catch {
            if (!this.destroyed && this.embeddedReport === report) {
                this.showReportUnavailable();
            }
        } finally {
            this.isRefreshingToken = false;
        }
    }

    private showReportUnavailable(): void {
        this.reportUnavailable = true;
        this.clearTokenRefreshTimer();

        if (this.reportContainer) {
            this.powerBiService.reset(this.reportContainer.nativeElement);
        }

        this.embeddedReport = null;
    }

    private clearTokenRefreshTimer(): void {
        if (this.tokenRefreshTimer !== null) {
            window.clearTimeout(this.tokenRefreshTimer);
            this.tokenRefreshTimer = null;
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.clearTokenRefreshTimer();

        if (this.reportContainer) {
            this.powerBiService.reset(this.reportContainer.nativeElement);
        }

        this.embeddedReport = null;
    }
}
